// Settings of the core and apps by groups (R-C13): a value for everyone, exceptions for roles, people and sections. Each group is a
// page of «Настройки» on the one template (DESIGN.md «Настройки»): one block of rows, the name and its explanation on the left, the
// control on the right, the exceptions right under their setting.

import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { canRead, managesSettings, resolveSetting, withOptions, type Id, type MetaIndex, type SettingDecl, type SettingDef, type SettingScope, type SettingValue } from '@confdeck/core-shared';
import { useAccess, useMeta, useSync } from '../sync/react.tsx';
import { navigate, typeHref } from '../shell/router.ts';
import { PageBlock } from '../ui/Page.tsx';
import { Button, ConfirmDialog, Field, IconButton, Input, Popover, PopoverContent, PopoverTrigger, PickList, Segmented, Select, Switch, cx, useToast } from '../ui/primitives.tsx';
import { FeaturesPane } from './FeaturesPane.tsx';
import { groupSlugs } from './group-slugs.ts';
import { controlOf, defaultText, exceptionScopes, exceptionSections, exceptionWho, offReason, valueText } from './rows.ts';
import s from './settings.module.css';

export const FEATURES = 'Модули и функции';

type Where = { scope: SettingScope; targetId?: Id | null; typeId?: Id | null };

/** Who may change settings for everyone; the others see their personal settings only (R-C13). */
export function useManage() {
  // The same rule as the server (B3): the menu shows nothing that opens a refusal.
  return managesSettings(useAccess());
}

/** The settings a person sees on the settings screen: all of them with the right, else their personal ones. */
export function useVisibleDecls(): SettingDecl[] {
  const client = useSync();
  const meta = useMeta();
  const manage = useManage();
  // A choice among terms offers the terms the workspace has now, not those of the moment the page loaded.
  return (client.header?.settings ?? []).filter((d) => !d.hidden && (manage || d.personal)).map((d) => withOptions(meta, d));
}

/** Groups of settings the person sees, with the Latin key of their address: each is an item of «Настройки» (decision 031 п.3). */
export function useSettingGroups(): { name: string; slug: string }[] {
  const client = useSync();
  const decls = useVisibleDecls();
  // «Модули и функции» is the owner's (decision 025): the parts of apps the workspace works with.
  const owner = useMeta().user(client.header?.userId ?? null)?.role === 'owner';
  return useMemo(() => {
    const names = [...new Set(decls.map((d) => d.group)), ...(owner ? [FEATURES] : [])];
    const slugs = groupSlugs(names, decls, { [FEATURES]: 'features' });
    return names.map((name) => ({ name, slug: slugs.get(name)! }));
  }, [decls.map((d) => `${d.group}:${d.key}`).join('|'), owner]);
}

// The setting the search of «Настройки» led to: its row scrolls into view and lights up once (decision 038 п.3).
let focused: string | null = null;
const focusListeners = new Set<() => void>();
export function focusSetting(key: string | null) { focused = key; for (const l of focusListeners) l(); }
const useFocused = () => useSyncExternalStore((cb) => { focusListeners.add(cb); return () => focusListeners.delete(cb); }, () => focused);

/** One group of settings: one block of rows; without the right — the person's own values of the personal settings. */
export function SettingsGroup({ group }: { group: string }) {
  const client = useSync();
  const meta = useMeta();
  const manage = useManage();
  const visible = useVisibleDecls();
  const all = client.header?.settings ?? [];
  const decls = visible.filter((d) => d.group === group);
  // The value for everyone of a key, or its default: what a dependent setting looks at (B9).
  const forAll = (key: string) => {
    const d = all.find((x) => x.key === key);
    return d ? meta.settings(key).find((v) => v.scope === 'all' && !v.targetId && !v.typeId)?.value ?? d.default : undefined;
  };
  return (
    <>
      {group === FEATURES && <FeaturesPane />}
      {decls.length > 0 && (
        <PageBlock rows>
          {decls.map((d) => <SettingRow key={d.key} d={d} manage={manage} off={offReason(d, all, forAll)} />)}
        </PageBlock>
      )}
    </>
  );
}

function SettingRow({ d, manage, off }: { d: SettingDecl; manage: boolean; off: string | null }) {
  const meta = useMeta();
  const client = useSync();
  const access = useAccess();
  const toast = useToast();
  const me = client.header?.userId ?? null;
  const vals = meta.settings(d.key);
  const forAll = vals.find((v) => v.scope === 'all' && !v.typeId);
  const mine = resolveSetting(meta, d, meta.user(me));
  // Values of records (a project, an event) are on their own tabs (R-E7), not exceptions of the account.
  const exceptions = manage ? vals.filter((v) => v.scope !== 'record' && (v.scope !== 'all' || v.typeId)) : [];
  const canAdd = manage && (exceptionScopes(d).length > 0 || d.perType);
  const go = async (cmd: 'setting.set' | 'setting.clear', where: Where, value?: SettingValue) => {
    const r = await client.run(cmd, { key: d.key, scope: where.scope, targetId: where.targetId ?? null, typeId: where.typeId ?? null, ...(cmd === 'setting.set' ? { value } : {}) });
    if (!r.ok) toast({ kind: 'error', text: r.error.message });
  };
  // Without the right the row is the person's own value; with it — the value for everyone.
  const value = manage ? forAll?.value ?? d.default : mine.value;
  const set = (v: SettingValue) => go('setting.set', manage ? { scope: 'all' } : { scope: 'user', targetId: me }, v);
  const own = manage ? forAll : mine.from === 'user' ? mine.def : null;
  const label = (v: SettingDef) => {
    const who = v.scope === 'all' ? 'Все' : v.scope === 'role' ? `Роль «${meta.role(v.targetId)?.name ?? '—'}»` : v.targetId === me ? 'Мне' : meta.user(v.targetId)?.name ?? 'Человек';
    return v.typeId ? `${who} · ${meta.type(v.typeId)?.nameMany ?? 'раздел'}` : who;
  };
  const section = useSection(d, access);
  const focus = useFocused();
  const row = useRef<HTMLDivElement>(null);
  const [lit, setLit] = useState(false);
  useEffect(() => {
    if (focus !== d.key) return;
    row.current?.scrollIntoView({ block: 'center' });
    setLit(true);
    focusSetting(null);
    const t = setTimeout(() => setLit(false), 1600);
    return () => clearTimeout(t);
  }, [focus, d.key]);
  const hint = (
    <>
      {d.description}
      {!own && ` ${defaultText(d)}`}
      {!manage && mine.from !== 'user' && mine.from !== 'default' && ' Сейчас — как у всех.'}
      {off && <span className={s.off}> {off}</span>}
      {d.records?.length ? ` ${recordsHint(meta, d)}` : ''}
      {(own || canAdd || section) && (
        <span className={s.rowActions}>
          {own && <Button size="sm" variant="ghost" onClick={() => void go('setting.clear', own)}>{manage ? 'Сбросить' : 'Как у всех'}</Button>}
          {canAdd && <AddException d={d} onAdd={(w) => void go('setting.set', w, resolveSetting(meta, d, w.scope === 'user' ? meta.user(w.targetId) : null, w.typeId).value)} />}
          {section && <Button size="sm" variant="ghost" icon="open" onClick={() => navigate(section.href)}>Открыть раздел «{section.label}»</Button>}
        </span>
      )}
    </>
  );
  return (
    <div ref={row} className={cx(s.setting, lit && s.lit)} data-setting={d.key}>
      <Field as="div" inline="end" label={d.name} hint={hint}>
        <ValueControl d={d} value={value} disabled={!!off} onChange={set} />
      </Field>
      {exceptions.length > 0 && (
        <div className={s.exceptions} aria-label={`Исключения: ${d.name}`}>
          {exceptions.map((v) => (
            <Field key={v.id} as="div" inline="end" label={label(v)} className={s.exception}>
              <span className={s.exceptionCtl}>
                <ValueControl d={d} value={v.value} onChange={(x) => void go('setting.set', v, x)} />
                <IconButton size="sm" icon="close" label="Как у всех" onClick={() => void go('setting.clear', v)} />
              </span>
            </Field>
          ))}
        </div>
      )}
    </div>
  );
}

/** Where else the setting is set (R-E7): «Своё значение — в настройках записей: Проекты, События». */
function recordsHint(meta: MetaIndex, d: SettingDecl): string {
  const names = (d.records ?? []).map((t) => meta.type(t)?.nameMany).filter(Boolean);
  return names.length ? `Своё значение задаётся во вкладке «Настройки»: ${names.join(', ')}.` : '';
}

/** «Открыть раздел»: the section the setting is about, when the person reads it (decision 038 п.5). */
function useSection(d: SettingDecl, access: ReturnType<typeof useAccess>): { href: string; label: string } | null {
  const meta = useMeta();
  const client = useSync();
  if (d.section?.type) {
    const t = meta.type(d.section.type);
    return t && canRead(access, t.id) ? { href: typeHref(t.key), label: t.nameMany } : null;
  }
  if (d.section?.screen) {
    const sc = client.menu.flatMap((m) => [...(m.screens ?? []), ...(m.groups ?? []).flatMap((g) => g.screens ?? [])]).find((x) => x.key === d.section!.screen);
    return sc ? { href: `/s/${sc.key}`, label: sc.label } : null;
  }
  return null;
}

/** The control of a value; a value that asks first (the gateway) opens the question and changes only after «Сменить». */
export function ValueControl({ d, value, onChange, disabled }: { d: SettingDecl; value: SettingValue; onChange: (v: SettingValue) => void; disabled?: boolean }) {
  const [draft, setDraft] = useState<string | null>(null);
  const [asking, setAsking] = useState<SettingValue | null>(null);
  const change = (v: SettingValue) => { if (v === value) return; if (d.confirm?.[String(v)]) setAsking(v); else onChange(v); };
  const control = controlOf(d);
  let el;
  if (control === 'switch') el = <Switch checked={value === true} disabled={disabled} onChange={change} label={d.name} />;
  else if (control === 'number') {
    const commit = () => { if (draft !== null && draft !== String(value) && draft.trim() !== '') change(Number(draft)); setDraft(null); };
    el = (
      <span className={s.number}>
        <Input type="number" className={s.numberInput} min={d.min} max={d.max} value={draft ?? String(value)} aria-label={d.name} disabled={disabled}
          onChange={(e) => setDraft(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') commit(); }} />
        {d.unit && <span className={s.unit}>{d.unit}</span>}
      </span>
    );
  } else if (control === 'text') {
    const commit = () => { if (draft !== null && draft !== String(value)) change(draft.trim()); setDraft(null); };
    el = <Input className={s.text} value={draft ?? String(value)} aria-label={d.name} disabled={disabled} placeholder="Не задано"
      onChange={(e) => setDraft(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') commit(); }} />;
  } else if (control === 'several') {
    el = <Several d={d} value={value} disabled={disabled} onChange={change} />;
  } else {
    const items = (d.options ?? []).map((o) => ({ value: o.value, label: o.label }));
    el = control === 'select'
      ? <Select label={d.name} value={String(value)} options={items} onChange={change} disabled={disabled} search={items.length > 8} className={s.select} />
      : <Segmented size="sm" label={d.name} value={String(value)} onChange={change} items={items.map((o) => ({ ...o, disabled }))} />;
  }
  return (
    <>
      {el}
      {asking !== null && (
        <ConfirmDialog open title={`${d.name}: ${valueText(d, asking)}?`} action="Сменить" onClose={() => setAsking(null)}
          onConfirm={() => { onChange(asking); setAsking(null); }}>
          <p>{d.confirm?.[String(asking)]}</p>
          <p>Сейчас — {valueText(d, value)}.</p>
        </ConfirmDialog>
      )}
    </>
  );
}

/** A choice of several options: the chosen ones in a line, a list of all of them to tick; the last one is not taken off. */
function Several({ d, value, onChange, disabled }: { d: SettingDecl; value: SettingValue; onChange: (v: SettingValue) => void; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const keys = String(value).split(',').filter(Boolean);
  const toggle = (k: string) => {
    const next = keys.includes(k) ? keys.filter((x) => x !== k) : [...keys, k];
    if (!next.length) return;
    onChange((d.options ?? []).map((o) => o.value).filter((x) => next.includes(x)).join(','));
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild><Button size="sm" disabled={disabled} aria-label={d.name} className={s.several}>{valueText(d, value)}</Button></PopoverTrigger>
      <PopoverContent width={320}>
        <PickList multiple options={(d.options ?? []).map((o) => ({ id: o.value, label: o.label }))} selected={keys} onPick={toggle} placeholder="Найти" onDone={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}

/** «+ Исключение»: a role or a person, and for per-section settings a section; the new exception starts with the working value. */
function AddException({ d, onAdd }: { d: SettingDecl; onAdd: (w: { scope: SettingScope; targetId: Id | null; typeId: Id | null }) => void }) {
  const meta = useMeta();
  const [open, setOpen] = useState(false);
  const [who, setWho] = useState<{ scope: SettingScope; targetId: Id | null } | null>(null);
  const options = exceptionWho(d, meta.roles(), meta.users().filter((u) => !u.archivedAt));
  const done = (w: { scope: SettingScope; targetId: Id | null }, typeId: Id | null) => { onAdd({ ...w, typeId }); setOpen(false); setWho(null); };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setWho(null); }}>
      <PopoverTrigger asChild><Button size="sm" variant="ghost" icon="plus">Исключение</Button></PopoverTrigger>
      <PopoverContent width={320}>
        {!who
          ? <PickList options={options} selected={[]} placeholder="Роль или человек" onPick={(id) => {
            const [scope, targetId] = id.split(':') as [SettingScope, string];
            const w = { scope, targetId: targetId || null };
            if (d.perType) setWho(w); else done(w, null);
          }} />
          : <PickList options={exceptionSections(who.scope, meta.types())} selected={[]} placeholder="Раздел" onPick={(id) => done(who, id || null)} />}
      </PopoverContent>
    </Popover>
  );
}
